import { useContext, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { highlightSearch, usePopperSelectDropdown } from '../../../ui/utils'
import { getOperatorTypes, isMultiValueRule, isTextMatchRule, matchesExactCase, VariablesContext, RuleEditProps, getRuleTypes, RuleErrorsContext, scrollIntoViewVertically } from './RuleHelpers'
import { Button, ButtonGroup } from '../../../ui'
import { SingleSelect } from '../../../ui/form/SingleSelect'
import { Combobox } from '@headlessui/react'
import { ChevronUpDownIcon, ExclamationCircleSolidIcon } from '../../../ui/icons'
import clsx from 'clsx'
import TextInput from '../../../ui/form/TextInput'
import TagInput from '../../../ui/form/TagInput'
import { Rule, RulePath } from '../../../types'
import { Tooltip } from '../../../ui/Tooltip'

export default function FilterRuleEdit({
    rule,
    setRule,
    group,
    eventName = '',
    controls,
}: Omit<RuleEditProps, 'root' | 'headerPrefix' | 'depth'>) {
    const { t } = useTranslation()
    const {
        setReferenceElement,
        setPopperElement,
        attributes,
        styles,
    } = usePopperSelectDropdown()
    const { suggestions } = useContext(VariablesContext)
    const { errors, clearError } = useContext(RuleErrorsContext)
    const error = errors[rule.uuid]
    const ruleRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        if (error && ruleRef.current) scrollIntoViewVertically(ruleRef.current)
    }, [error])
    const errorIndicator = error && (
        <Tooltip content={error.charAt(0).toUpperCase() + error.slice(1)} variant="error">
            <span className="rule-error-indicator" role="img" aria-label={error} tabIndex={0}>
                <ExclamationCircleSolidIcon />
            </span>
        </Tooltip>
    )
    const setValue = (value: Rule['value']) => {
        if (error) clearError(rule.uuid)
        setRule({ ...rule, value })
    }
    const { path } = rule
    const isJourney = rule.group === 'journey'
    const hasValue = rule?.operator && !['is set', 'is not set', 'empty'].includes(rule?.operator)
    const withType = (type: typeof rule.type) => type === 'boolean' ? { ...rule, type, value: 'true' } : { ...rule, type }
    const valuePlaceholder = rule.type === 'version' ? t('rule_version_value_placeholder') : t('value')
    // Limit options to prevent Headless UI focus-management performance issues
    // (sortByDomNode uses O(n²) compareDocumentPosition calls)
    const MAX_OPTIONS = 50

    const pathSuggestions = useMemo<RulePath[]>(() => {
        if (isJourney) return []
        let paths = group === 'event'
            ? eventName
                ? suggestions.eventPaths[eventName] ?? []
                : []
            : suggestions.userPaths

        if (path) {
            let search = path.toLowerCase()
            if (search.startsWith('.')) search = '$' + search
            if (!search.startsWith('$.')) search = '$.' + search
            paths = paths.filter(p => p.path.toLowerCase().startsWith(search))
        }

        return paths.slice(0, MAX_OPTIONS)
    }, [suggestions, group, isJourney, eventName, path])

    return (
        <div className={clsx('rule', error && 'rule-invalid')} ref={ruleRef}>
            <ButtonGroup className="ui-select">
                <SingleSelect
                    value={rule.type}
                    onChange={type => setRule(withType(type))}
                    options={getRuleTypes(t)}
                    required
                    hideLabel
                    size="small"
                    toValue={x => x.key as typeof rule.type}
                />
                <Combobox onChange={(value: RulePath | null) => value && setRule({ ...withType(value.data_type), path: value.path })}>
                    <span className="ui-text-input">
                        <Combobox.Input
                            value={rule.path}
                            onChange={e => setRule({ ...rule, path: e.target.value })}
                            required
                            placeholder={isJourney ? t('rule_journey_path_placeholder') : undefined}
                            ref={setReferenceElement}
                            className="small"
                        />
                    </span>
                    <Combobox.Button className="ui-button small secondary">
                        <ChevronUpDownIcon />
                    </Combobox.Button>
                    <Combobox.Options
                        className="select-options nowheel"
                        ref={setPopperElement}
                        style={styles.popper}
                        {...attributes.popper}
                    >
                        {
                            pathSuggestions.length
                                ? pathSuggestions.map(s => (
                                    <Combobox.Option
                                        key={s.path}
                                        value={s}
                                        className={({ focus, selected }) => clsx('select-option', focus && 'active', selected && 'selected')}
                                    >
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: highlightSearch(s.path, rule.path),
                                            }}
                                        />
                                    </Combobox.Option>
                                ))
                                : <li className="select-option-empty">No Results</li>
                        }
                    </Combobox.Options>
                </Combobox>
                <SingleSelect
                    value={rule.operator}
                    onChange={operator => {
                        let value = rule.value
                        const wasMulti = isMultiValueRule(rule.type, rule.operator)
                        const isMulti = isMultiValueRule(rule.type, operator)
                        if (wasMulti && !isMulti && Array.isArray(value)) {
                            value = value[0] ?? ''
                        } else if (!wasMulti && isMulti && !Array.isArray(value)) {
                            value = value ? [value] : []
                        }
                        setRule({ ...rule, operator, value })
                    }}
                    options={getOperatorTypes(t)[rule.type] ?? []}
                    required
                    hideLabel
                    size="small"
                    toValue={x => x.key}
                />
                { hasValue && <span className="rule-value">
                    {isMultiValueRule(rule.type, rule.operator)
                        ? <>
                            <TagInput
                                size="small"
                                placeholder={valuePlaceholder}
                                value={Array.isArray(rule.value) ? rule.value : rule.value ? [rule.value] : []}
                                onChange={setValue}
                            />
                            {errorIndicator && <div className="ui-text-input-suffix small">{errorIndicator}</div>}
                        </>
                        : <TextInput
                            size="small"
                            type="text"
                            name="value"
                            placeholder={valuePlaceholder}
                            disabled={rule.type === 'boolean'}
                            hideLabel={true}
                            value={rule.type === 'boolean' ? 'true' : (Array.isArray(rule.value) ? rule.value[0] ?? '' : rule?.value?.toString())}
                            onChange={setValue}
                            suffix={errorIndicator || undefined}
                        />}
                </span>}
            </ButtonGroup>

            <div className="rule-controls">
                { hasValue && isTextMatchRule(rule) && <Button
                    size="small"
                    variant={matchesExactCase(rule) ? 'primary' : 'plain'}
                    title={t('rule_match_exact_case_hint')}
                    aria-label={t('rule_match_exact_case')}
                    aria-pressed={matchesExactCase(rule)}
                    onClick={() => setRule({ ...rule, case_sensitive: !matchesExactCase(rule) })}
                >
                    {t('rule_match_exact_case_short')}
                </Button>}
                {controls}
            </div>
        </div>
    )
}
